import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { diffMdLines, groupDiffHunks, parseMdLine, wordDiffHtml } from '../../../utils/diffUtils';
import { diffActionBtnsHtml } from '../DiffActionBtns/DiffActionBtns';
import './DescriptionDiffView.css';

const DescriptionDiffView = ({ original, suggested, changeId, onAccept, onReject, disabled, reasoning, className }) => {
  const containerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const activeHunkRef = useRef(null);
  const [hunkStates, setHunkStates] = useState({});

  const { diffData, totalHunks } = useMemo(() => {
    const oldL = (original || '').split('\n').filter(l => l.trim());
    const newL = (suggested || '').split('\n').filter(l => l.trim());
    const ops = diffMdLines(oldL, newL);
    const hunks = groupDiffHunks(ops);
    const total = hunks.filter(h => h.type === 'change').length;
    return { diffData: { ops, hunks, oldLines: oldL, newLines: newL }, totalHunks: total };
  }, [original, suggested]);

  const elements = useMemo(() => {
    const { hunks } = diffData;
    let hIdx = 0;
    const els = [];

    for (const hunk of hunks) {
      if (hunk.type === 'equal') {
        for (const op of hunk.lines) {
          const p = parseMdLine(op.value);
          if (p) els.push({ tag: p.isList ? 'li' : 'p', html: p.html, cls: '', hunk: 0 });
        }
      } else {
        hIdx++;
        const state = hunkStates[hIdx] || 'pending';
        const rem = hunk.lines.filter(l => l.type === 'removed');
        const add = hunk.lines.filter(l => l.type === 'added');

        if (state === 'accepted') {
          for (const a of add) {
            const p = parseMdLine(a.value);
            if (p) els.push({ tag: p.isList ? 'li' : 'p', html: p.html, cls: '', hunk: 0 });
          }
        } else if (state === 'rejected') {
          for (const r of rem) {
            const p = parseMdLine(r.value);
            if (p) els.push({ tag: p.isList ? 'li' : 'p', html: p.html, cls: '', hunk: 0 });
          }
        } else {
          const hunkEls = [];
          for (const r of rem) {
            const pr = parseMdLine(r.value);
            if (!pr) continue;
            const paired = add.length === rem.length ? add[rem.indexOf(r)] : null;
            const wd = paired ? wordDiffHtml(r.value, paired.value) : null;
            hunkEls.push({ tag: pr.isList ? 'li' : 'p', html: wd ? wd.rem : pr.html, cls: 'diff-line-removed', hunk: hIdx });
          }
          for (const a of add) {
            const pa = parseMdLine(a.value);
            if (!pa) continue;
            const paired = rem.length === add.length ? rem[add.indexOf(a)] : null;
            const wd = paired ? wordDiffHtml(paired.value, a.value) : null;
            hunkEls.push({ tag: pa.isList ? 'li' : 'p', html: wd ? wd.add : pa.html, cls: 'diff-line-added', hunk: hIdx });
          }
          if (hunkEls.length > 0) hunkEls[hunkEls.length - 1].isLast = true;
          els.push(...hunkEls);
        }
      }
    }
    return els;
  }, [diffData, hunkStates]);

  const resolveHunk = useCallback((hunkId, action) => {
    setHunkStates(prev => {
      const next = { ...prev, [hunkId]: action };
      const { hunks } = diffData;
      let hIdx = 0;
      let allResolved = true;
      const mergedLines = [];
      for (const hunk of hunks) {
        if (hunk.type === 'equal') {
          for (const op of hunk.lines) mergedLines.push(op.value);
        } else {
          hIdx++;
          const st = next[hIdx] || 'pending';
          if (st === 'pending') { allResolved = false; break; }
          const rem = hunk.lines.filter(l => l.type === 'removed');
          const add = hunk.lines.filter(l => l.type === 'added');
          if (st === 'accepted') { for (const a of add) mergedLines.push(a.value); }
          else { for (const r of rem) mergedLines.push(r.value); }
        }
      }
      if (allResolved) {
        const allAccepted = Object.values(next).every(s => s === 'accepted');
        const allRejected = Object.values(next).every(s => s === 'rejected');
        if (allRejected) {
          setTimeout(() => onReject(changeId), 0);
        } else if (allAccepted) {
          setTimeout(() => onAccept(changeId), 0);
        } else {
          setTimeout(() => onAccept(changeId, mergedLines.join('\n')), 0);
        }
      }
      return next;
    });
  }, [diffData, changeId, onAccept, onReject]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const showHunk = (hid) => {
      if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
      if (hid === activeHunkRef.current) return;
      container.querySelectorAll('.cursor-hunk-toolbar').forEach(tb => { tb.style.display = 'none'; });
      activeHunkRef.current = hid;
      if (hid) {
        const tb = container.querySelector(`.cursor-hunk-toolbar[data-toolbar="${hid}"]`);
        if (tb) tb.style.display = 'flex';
      }
    };

    const scheduleHide = () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        container.querySelectorAll('.cursor-hunk-toolbar').forEach(tb => { tb.style.display = 'none'; });
        activeHunkRef.current = null;
        hideTimerRef.current = null;
      }, 80);
    };

    const onOver = (e) => {
      const el = e.target.closest('[data-hunk]');
      if (el) showHunk(el.dataset.hunk);
    };
    const onOut = (e) => {
      const el = e.target.closest('[data-hunk]');
      if (el) {
        const related = e.relatedTarget;
        if (related && related.closest && related.closest(`[data-hunk="${el.dataset.hunk}"]`)) return;
        scheduleHide();
      }
    };
    const onLeave = () => scheduleHide();
    const onClick = (e) => {
      const btn = e.target.closest('[data-diff-action]');
      if (!btn || disabled) return;
      const hunkId = btn.dataset.hunkId;
      const action = btn.dataset.diffAction;
      if (action === 'keep') resolveHunk(hunkId, 'accepted');
      else if (action === 'undo') resolveHunk(hunkId, 'rejected');
    };

    container.addEventListener('mouseover', onOver);
    container.addEventListener('mouseout', onOut);
    container.addEventListener('mouseleave', onLeave);
    container.addEventListener('click', onClick);
    return () => {
      container.removeEventListener('mouseover', onOver);
      container.removeEventListener('mouseout', onOut);
      container.removeEventListener('mouseleave', onLeave);
      container.removeEventListener('click', onClick);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [disabled, resolveHunk]);

  const buildHtml = () => {
    let html = '', inUl = false;
    for (const el of elements) {
      if (el.tag === 'li' && !inUl) { html += '<ul>'; inUl = true; }
      if (el.tag === 'p' && inUl) { html += '</ul>'; inUl = false; }

      const cls = el.cls ? ` class="${el.cls}"` : '';
      const hunkAttr = el.hunk ? ` data-hunk="${el.hunk}"` : '';

      if (el.isLast) {
        html += `<${el.tag}${cls}${hunkAttr}>${el.html}`;
        html += `<span class="cursor-hunk-toolbar" data-toolbar="${el.hunk}" style="display:none">`;
        html += `<span class="cursor-hunk-nav">${el.hunk} of ${totalHunks}</span>`;
        html += diffActionBtnsHtml({
          acceptAttrs: `data-diff-action="keep" data-hunk-id="${el.hunk}"`,
          rejectAttrs: `data-diff-action="undo" data-hunk-id="${el.hunk}"`,
        });
        html += `</span>`;
        html += `</${el.tag}>`;
      } else {
        html += `<${el.tag}${cls}${hunkAttr}>${el.html}</${el.tag}>`;
      }
    }
    if (inUl) html += '</ul>';
    return html;
  };

  return (
    <div className="cursor-diff-wrapper">
      {reasoning && (
        <div className="cursor-diff-reasoning">{reasoning}</div>
      )}
      <div
        ref={containerRef}
        className={`cursor-diff-container${className ? ` ${className}` : ''}`}
        dangerouslySetInnerHTML={{ __html: buildHtml() }}
      />
    </div>
  );
};

export default DescriptionDiffView;
